/**
 * Settings Panel — Workdays, shift hours, service time, depot, budget,
 * plus expanded fields: complex service time, chem refill, lunch break.
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import * as api from '../services/routeOptimizerService';

const ALL_DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

export default function SettingsPanel({ entityId, entityType = 'week', settings: initialSettings, onRefresh, onProceed, onReset, resetLabel }) {
  const { user } = useAuth();
  const userLevel = user?.level ?? 3;
  const canEditDefaults = userLevel <= 1; // Levels 0-1 can edit company/branch defaults
  const readOnly = !canEditDefaults && entityType !== 'week'; // Branch/company managers can edit week settings but not company/branch defaults
  const [settings, setSettings] = useState(initialSettings || {});
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [message, setMessage] = useState('');
  const [depotAddress, setDepotAddress] = useState('');
  const [geocoding, setGeocoding] = useState(false);
  const [chemAddress, setChemAddress] = useState('');
  const [chemGeocoding, setChemGeocoding] = useState(false);

  useEffect(() => {
    if (initialSettings) setSettings(initialSettings);
  }, [initialSettings]);

  // Reverse-geocode depot coordinates on mount when address text is empty
  useEffect(() => {
    if (depotAddress || !initialSettings?.depot_lat || !initialSettings?.depot_lon) return;
    const lat = initialSettings.depot_lat;
    const lon = initialSettings.depot_lon;
    fetch(`https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lon}&format=json`, {
      headers: { 'User-Agent': 'CERTUSRouteOptimizer/1.0' }
    })
      .then(r => r.json())
      .then(data => { if (data.display_name) setDepotAddress(data.display_name); })
      .catch(() => {});
  }, [initialSettings?.depot_lat, initialSettings?.depot_lon]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = (key, value) => {
    if (readOnly) return;
    setSettings(prev => ({ ...prev, [key]: value }));
    setMessage('');
  };

  const toggleDay = (day) => {
    if (readOnly) return;
    const current = settings.workdays || [];
    const next = current.includes(day)
      ? current.filter(d => d !== day)
      : [...current, day];
    handleChange('workdays', next);
  };

  const geocodeAddress = async (address, setAddr, setLoading, latKey, lonKey, displayCallback) => {
    if (!address.trim()) return;
    setLoading(true);
    setMessage('');
    try {
      const q = encodeURIComponent(address.trim());
      const res = await fetch(`https://nominatim.openstreetmap.org/search?q=${q}&format=json&limit=1`, {
        headers: { 'User-Agent': 'CERTUSRouteOptimizer/1.0' }
      });
      const data = await res.json();
      if (data.length === 0) {
        setMessage('Address not found. Try a more specific address.');
        return;
      }
      const { lat, lon, display_name } = data[0];
      setSettings(prev => ({ ...prev, [latKey]: parseFloat(lat), [lonKey]: parseFloat(lon) }));
      setAddr(display_name);
      if (displayCallback) displayCallback(display_name);
      else setMessage(`Location set to: ${display_name}`);
    } catch (err) {
      setMessage(`Geocoding error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (entityType === 'company') await api.saveCompanySettings(entityId, settings);
      else if (entityType === 'branch') await api.saveBranchSettings(entityId, settings);
      else await api.saveSettings(entityId, settings);
      setMessage('Settings saved');
      if (onRefresh) onRefresh();
    } catch (err) {
      setMessage(`Error: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="ro-panel">
      <h3>Optimization Settings</h3>
      {readOnly && (
        <p className="ro-settings-info" style={{ color: 'var(--ro-warning-text, #92400e)' }}>
          These settings are managed by administrators. Contact your admin to make changes.
        </p>
      )}

      <fieldset disabled={readOnly} style={{ border: 'none', padding: 0, margin: 0 }}>
      <div className="ro-form-grid">
        {/* Workdays */}
        <div className="ro-form-group">
          <label>Workdays</label>
          <div className="ro-day-toggles">
            {ALL_DAYS.map(day => (
              <button
                key={day}
                className={`ro-day-btn ${(settings.workdays || []).includes(day) ? 'ro-day-active' : ''}`}
                onClick={() => toggleDay(day)}
                aria-pressed={(settings.workdays || []).includes(day)}
                aria-label={`${day} workday toggle`}
              >
                {day.substring(0, 3)}
              </button>
            ))}
          </div>
        </div>

        {/* Shift & Service */}
        <h4 className="ro-subsection-title">Shift Schedule</h4>
        <div className="ro-form-row">
          <div className="ro-form-group">
            <label>Shift Hours</label>
            <input
              type="number" min="1" max="24" step="0.5"
              value={settings.shift_hours ?? 8}
              onChange={e => handleChange('shift_hours', parseFloat(e.target.value))}
              className="ro-input"
            />
          </div>
          <div className="ro-form-group">
            <label>Shift End Time</label>
            <input
              type="time"
              value={settings.shift_end_time ?? '16:30'}
              onChange={e => handleChange('shift_end_time', e.target.value)}
              className="ro-input"
            />
          </div>
          <div className="ro-form-group">
            <label>Computed Start</label>
            <input
              type="text"
              disabled
              className="ro-input"
              value={(() => {
                const end = settings.shift_end_time || '16:30';
                const [h, m] = end.split(':').map(Number);
                const shiftMin = (settings.shift_hours ?? 8) * 60;
                const totalMin = h * 60 + m - shiftMin;
                const sh = Math.floor((totalMin + 1440) % 1440 / 60);
                const sm = (totalMin + 1440) % 1440 % 60;
                const ampm = sh >= 12 ? 'PM' : 'AM';
                const h12 = sh > 12 ? sh - 12 : sh === 0 ? 12 : sh;
                return `${h12}:${String(sm).padStart(2, '0')} ${ampm}`;
              })()}
            />
          </div>
        </div>

        <h4 className="ro-subsection-title">Service Times</h4>
        <div className="ro-form-row">
          <div className="ro-form-group">
            <label>Standard (min/stop)</label>
            <input
              type="number" min="1" max="120"
              value={settings.service_minutes ?? 20}
              onChange={e => handleChange('service_minutes', parseInt(e.target.value))}
              className="ro-input"
            />
          </div>
          <div className="ro-form-group">
            <label>Complex (min/stop)</label>
            <input
              type="number" min="1" max="180"
              value={settings.complex_service_minutes ?? (settings.service_minutes ?? 30)}
              onChange={e => handleChange('complex_service_minutes', parseInt(e.target.value))}
              className="ro-input"
            />
          </div>
          <div className="ro-form-group">
            <label>Max Stops / Tech / Day</label>
            <input
              type="number" min="1" max="100"
              value={settings.max_stops_per_day ?? ''}
              placeholder="No limit"
              onChange={e => handleChange('max_stops_per_day', e.target.value ? parseInt(e.target.value) : null)}
              className="ro-input"
            />
          </div>
        </div>

        {/* Depot */}
        <hr />
        <h4 className="ro-subsection-title">Depot / HQ Location</h4>

        <div className="ro-form-group">
          <label>Depot Address</label>
          <div className="ro-search-row">
            <input
              type="text"
              placeholder="e.g. 123 Main St, Tampa, FL"
              value={depotAddress}
              onChange={e => setDepotAddress(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && geocodeAddress(depotAddress, setDepotAddress, setGeocoding, 'depot_lat', 'depot_lon')}
              className="ro-input"
              aria-label="Depot address for geocoding lookup"
            />
            <button
              className="ro-btn ro-btn-secondary"
              onClick={() => geocodeAddress(depotAddress, setDepotAddress, setGeocoding, 'depot_lat', 'depot_lon')}
              disabled={geocoding || !depotAddress.trim()}
            >
              {geocoding ? <><span className="ro-geocoding-spinner" /> Looking up...</> : 'Lookup'}
            </button>
          </div>
        </div>
        <div className="ro-form-row">
          <div className="ro-form-group">
            <label>Depot Latitude</label>
            <input
              type="number" step="0.0001"
              value={settings.depot_lat ?? 28.9275}
              onChange={e => handleChange('depot_lat', parseFloat(e.target.value))}
              className="ro-input"
            />
          </div>
          <div className="ro-form-group">
            <label>Depot Longitude</label>
            <input
              type="number" step="0.0001"
              value={settings.depot_lon ?? -82.0373}
              onChange={e => handleChange('depot_lon', parseFloat(e.target.value))}
              className="ro-input"
            />
          </div>
        </div>

        {/* Chemical Refill Location */}
        <hr />
        <h4 className="ro-subsection-title">Chemical Refill Stop</h4>
        <p className="ro-form-description">
          Mandatory daily stop at a designated chemical supply location. All employees use the same refill point.
        </p>

        <div className="ro-form-group">
          <label>Refill Location Address</label>
          <div className="ro-search-row">
            <input
              type="text"
              placeholder="e.g. 456 Supply Rd, Tampa, FL"
              value={chemAddress}
              onChange={e => setChemAddress(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && geocodeAddress(chemAddress, setChemAddress, setChemGeocoding, 'chem_refill_lat', 'chem_refill_lon')}
              className="ro-input"
              aria-label="Chemical refill location address for geocoding lookup"
            />
            <button
              className="ro-btn ro-btn-secondary"
              onClick={() => geocodeAddress(chemAddress, setChemAddress, setChemGeocoding, 'chem_refill_lat', 'chem_refill_lon')}
              disabled={chemGeocoding || !chemAddress.trim()}
            >
              {chemGeocoding ? <><span className="ro-geocoding-spinner" /> Looking up...</> : 'Lookup'}
            </button>
          </div>
        </div>
        <div className="ro-form-row">
          <div className="ro-form-group">
            <label>Refill Latitude</label>
            <input
              type="number" step="0.0001"
              value={settings.chem_refill_lat ?? ''}
              onChange={e => handleChange('chem_refill_lat', parseFloat(e.target.value) || null)}
              className="ro-input"
              placeholder="—"
            />
          </div>
          <div className="ro-form-group">
            <label>Refill Longitude</label>
            <input
              type="number" step="0.0001"
              value={settings.chem_refill_lon ?? ''}
              onChange={e => handleChange('chem_refill_lon', parseFloat(e.target.value) || null)}
              className="ro-input"
              placeholder="—"
            />
          </div>
          <div className="ro-form-group">
            <label>Refill Duration (minutes)</label>
            <input
              type="number" min="1" max="120"
              value={settings.chem_refill_duration_minutes ?? 30}
              onChange={e => handleChange('chem_refill_duration_minutes', parseInt(e.target.value))}
              className="ro-input"
            />
          </div>
        </div>

        {/* Lunch Break */}
        <hr />
        <h4 className="ro-subsection-title">Lunch Break</h4>
        <p className="ro-form-description">
          Mandatory break scheduled by the optimizer. Can occur at any physical location.
        </p>

        <div className="ro-form-row">
          <div className="ro-form-group">
            <label>Duration (minutes)</label>
            <input
              type="number" min="0" max="120"
              value={settings.lunch_break_duration_minutes ?? 30}
              onChange={e => handleChange('lunch_break_duration_minutes', parseInt(e.target.value))}
              className="ro-input"
            />
          </div>
          <div className="ro-form-group">
            <label>Earliest Start</label>
            <input
              type="time"
              value={settings.lunch_break_earliest ?? '11:00'}
              onChange={e => handleChange('lunch_break_earliest', e.target.value)}
              className="ro-input"
            />
          </div>
          <div className="ro-form-group">
            <label>Latest Start</label>
            <input
              type="time"
              value={settings.lunch_break_latest ?? '14:00'}
              onChange={e => handleChange('lunch_break_latest', e.target.value)}
              className="ro-input"
            />
          </div>
        </div>
      </div>
      </fieldset>

      {!readOnly && <div className="ro-button-group">
        <button className="ro-btn ro-btn-primary" onClick={handleSave} disabled={saving || resetting}>
          {saving ? 'Saving...' : 'Save Settings'}
        </button>
        {entityType === 'week' && onProceed && (
          <button className="ro-btn ro-btn-primary" onClick={async () => { await handleSave(); if (onProceed) onProceed(); }} disabled={saving || resetting}>
            Save &amp; Proceed to Optimize
          </button>
        )}
        {onReset && (
          <button
            className="ro-btn ro-btn-secondary"
            onClick={async () => {
              if (!window.confirm(`Reset all settings to ${resetLabel?.replace('Reset to ', '') || 'parent defaults'}? This will overwrite current settings.`)) return;
              setResetting(true);
              try {
                await onReset();
                setMessage('Settings reset to defaults');
              } catch (err) {
                setMessage(`Error: ${err.message}`);
              } finally {
                setResetting(false);
              }
            }}
            disabled={saving || resetting}
          >
            {resetting ? 'Resetting...' : (resetLabel || 'Reset to Defaults')}
          </button>
        )}
      </div>}
      {message && (
        <div className={message.startsWith('Error') || message.startsWith('Geocoding error') ? 'ro-message-error' : message.startsWith('Address not found') ? 'ro-message' : 'ro-message-success'}>
          {message.replace(/^Error:\s*/, '')}
        </div>
      )}
    </div>
  );
}
